import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, Youtube, Handshake, Zap, TrendingUp, Share2, Twitter, Copy, Check } from "lucide-react";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { trackEvent } from "@/components/Analytics";

const POPULAR_BRANDS = ["Notion", "Riverside.fm", "1Password", "Squarespace", "Descript", "NordVPN", "Skillshare"];

export default function PublicFit() {
  const { slug: urlSlug } = useParams();
  const [channel, setChannel] = useState("");
  const [brand, setBrand] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [count, setCount] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loadingShared, setLoadingShared] = useState(false);

  useEffect(() => {
    api.get("/public/marketplace-count").then(({ data }) => setCount(data)).catch((e) => console.error("marketplace-count failed", e));
  }, []);

  // Load a shared result if slug in URL
  useEffect(() => {
    if (!urlSlug) return;
    setLoadingShared(true);
    setError("");
    api.get(`/public/fit/${urlSlug}`)
      .then(({ data }) => {
        setResult(data);
        setBrand(data.brand || "");
        setChannel(data.channel_title || "");
        trackEvent("public_fit_score_viewed_shared", { slug: urlSlug });
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "This shared fit score was not found.");
      })
      .finally(() => setLoadingShared(false));
  }, [urlSlug]);

  const run = async (e) => {
    e?.preventDefault();
    if (!channel.trim() || !brand.trim()) { toast.error("Enter both a channel and a brand"); return; }
    setLoading(true); setError(""); setResult(null);
    trackEvent("public_fit_score_started", { brand });
    try {
      const { data } = await api.post("/public/fit-score", { channel: channel.trim(), brand: brand.trim() });
      setResult(data);
      trackEvent("public_fit_score_completed", { brand, score: data.score });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setLoading(false); }
  };

  const verdictColor = (v) => v === "strong-fit" ? "#34C759" : v === "decent-fit" ? "#FFC107" : v === "weak-fit" ? "#FF9500" : "#FF3B30";

  // Share URL — points to /share/fit/:slug so social crawlers see OG tags
  const shareUrl = result?.slug
    ? `${window.location.origin}/api/share/fit/${result.slug}`
    : null;
  const prettyUrl = result?.slug
    ? `${window.location.origin}/fit/${result.slug}`
    : null;
  const ogImage = result?.slug
    ? `${window.location.origin}/api/og/fit/${result.slug}.png`
    : null;

  const copyShareLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      trackEvent("public_fit_share_copied", { slug: result?.slug });
      toast.success("Share link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      console.error("clipboard failed", e);
      toast.error("Copy failed — long-press the link to copy");
    }
  };

  const tweetIt = () => {
    if (!shareUrl || !result) return;
    const text = `I scored ${result.score}/100 as a fit for ${result.brand || brand} on @SourceDeck 🎯\n\nScore your channel free →`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    trackEvent("public_fit_share_tweet", { slug: result.slug });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Dynamic SEO for shared results (works for JS-aware crawlers; static OG server-side is at /api/share/fit/:slug)
  const seoTitle = result
    ? `${result.channel_title || channel} × ${result.brand || brand} — ${result.score}/100 fit · SourceDeck`
    : "Free AI Sponsor Fit Score — Test Any Creator × Any Brand · SourceDeck";
  const seoDesc = result?.alignment
    ? result.alignment.slice(0, 200)
    : "Paste any YouTube channel + any brand. Get an AI-powered fit score in 30 seconds. Free. No signup.";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white sd-grain">
      <SEO
        title={seoTitle}
        description={seoDesc}
        path={urlSlug ? `/fit/${urlSlug}` : "/fit"}
        image={ogImage || undefined}
      />

      <header className="sticky top-0 z-40 bg-[#0a0a0a]/70 backdrop-blur-xl border-b border-[#27272a]">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-[#FF3B30]" />
            <span className="font-display text-xl tracking-tight">SourceDeck</span>
          </Link>
          <Link to="/pricing" className="sd-btn sd-btn-primary h-9 text-xs">
            Start free trial <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </header>

      <section className="mx-auto max-w-3xl px-6 md:px-10 py-16 md:py-24">
        <div className="sd-label mb-6" data-testid="fit-eyebrow">
          <span className="text-[#FF3B30]">●</span>&nbsp;&nbsp;FREE · NO SIGNUP · POWERED BY CLAUDE SONNET 4.5
        </div>
        <h1 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight" data-testid="fit-h1">
          {urlSlug ? (
            <>Someone scored <br/><span className="italic text-zinc-400">this fit.</span></>
          ) : (
            <>Will this brand <br/><span className="italic text-zinc-400">actually sponsor you?</span></>
          )}
        </h1>
        <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-xl">
          Paste any YouTube channel and any brand. In 30 seconds, our AI scores audience fit,
          drafts a pitch angle, and calls out the risks — using real signals from the channel's uploads.
        </p>

        {count && !urlSlug && (
          <p className="mt-4 text-sm font-mono text-[#34C759]" data-testid="fit-count-hook">
            ▲ {count.actively_hiring} brands actively hiring creators this quarter. Score any of them free below.
          </p>
        )}

        {loadingShared && (
          <div className="mt-10 sd-card p-8 text-zinc-400 flex items-center gap-3" data-testid="fit-loading-shared">
            <Loader2 size={16} className="animate-spin" /> Loading shared fit score…
          </div>
        )}

        {!urlSlug && (
          <form onSubmit={run} className="mt-10 space-y-4">
            <div>
              <div className="sd-label mb-2 flex items-center gap-2"><Youtube size={11} strokeWidth={1.5} className="text-[#FF3B30]" /> YOUR YOUTUBE CHANNEL</div>
              <input
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                placeholder="@mkbhd — or paste the channel URL"
                className="sd-input h-12 w-full"
                data-testid="fit-channel-input"
                disabled={loading}
              />
            </div>
            <div>
              <div className="sd-label mb-2 flex items-center gap-2"><Handshake size={11} strokeWidth={1.5} className="text-[#FF3B30]" /> BRAND TO SCORE AGAINST</div>
              <input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="Notion, Squarespace, Rocket Money, or any brand"
                className="sd-input h-12 w-full"
                data-testid="fit-brand-input"
                disabled={loading}
                list="popular-brands"
              />
              <datalist id="popular-brands">
                {POPULAR_BRANDS.map((b) => <option key={b} value={b} />)}
              </datalist>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {POPULAR_BRANDS.slice(0, 6).map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setBrand(b)}
                    className="font-mono text-[10px] uppercase text-zinc-500 border border-[#27272a] px-2 py-1 hover:text-white hover:border-zinc-500 transition-colors"
                    data-testid={`fit-brand-chip-${b}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="sd-btn sd-btn-accent h-14 w-full text-base"
              data-testid="fit-submit-btn"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Scoring — this takes 20-30 sec</>
               : <><Sparkles size={16} strokeWidth={1.5} /> Score this fit — free</>}
            </button>
          </form>
        )}

        {error && (
          <div className="mt-8 border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] p-4 text-sm text-[#FF3B30] font-mono" data-testid="fit-error">
            × {error}
          </div>
        )}

        {result && (
          <div className="mt-12 sd-card p-8" data-testid="fit-result">
            <div className="sd-label mb-4 text-[#34C759]">● SCORE COMPUTED</div>
            <div className="flex items-center gap-4 mb-6">
              {result.channel_avatar && (
                <img src={result.channel_avatar} alt="" className="w-12 h-12 border border-[#27272a]" referrerPolicy="no-referrer" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-lg text-white truncate">{result.channel_title}</div>
                <div className="sd-label">{result.subscribers?.toLocaleString()} SUBS · × {(result.brand || brand).toUpperCase()}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start mb-6">
              <div className="font-display text-7xl md:text-8xl leading-none tabular-nums" style={{ color: verdictColor(result.verdict) }} data-testid="fit-score-value">
                {result.score}<span className="text-2xl md:text-3xl text-zinc-500">/100</span>
              </div>
              <div>
                <div className="font-mono text-xs uppercase tracking-widest mb-3" style={{ color: verdictColor(result.verdict) }}>
                  ● {result.verdict?.replace("-", " ")}
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed">{result.alignment}</p>
              </div>
            </div>

            {result.pitch_angle && (
              <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/[0.04] p-4 mb-4">
                <div className="sd-label mb-2 text-[#FF3B30]">SUGGESTED PITCH ANGLE</div>
                <p className="text-sm text-zinc-200 italic">"{result.pitch_angle}"</p>
              </div>
            )}
            {result.integration_format && (
              <div className="text-xs text-zinc-400 mb-4">
                <span className="text-zinc-500">Suggested format:</span> {result.integration_format}
              </div>
            )}
            {result.risks?.length > 0 && (
              <div className="mb-4">
                <div className="sd-label mb-2">RISKS TO CONSIDER</div>
                <ul className="space-y-1">
                  {result.risks.map((r, i) => <li key={`risk-${i}`} className="text-xs text-zinc-500 font-mono">— {r}</li>)}
                </ul>
              </div>
            )}

            {/* Share this result — viral loop */}
            {result.slug && (
              <div className="mt-6 pt-6 border-t border-[#27272a]">
                <div className="sd-label mb-3 flex items-center gap-2"><Share2 size={11} strokeWidth={1.5} /> SHARE THIS RESULT</div>
                <div className="flex flex-col md:flex-row gap-2">
                  <div className="flex-1 flex items-center gap-2 border border-[#27272a] bg-[#0e0e0f] px-3 h-11 min-w-0">
                    <span className="text-xs font-mono text-zinc-400 truncate" data-testid="fit-share-url">{prettyUrl}</span>
                  </div>
                  <button
                    onClick={copyShareLink}
                    className="sd-btn sd-btn-primary h-11 px-4"
                    data-testid="fit-share-copy-btn"
                  >
                    {copied ? <><Check size={14} strokeWidth={1.5} /> Copied</> : <><Copy size={14} strokeWidth={1.5} /> Copy link</>}
                  </button>
                  <button
                    onClick={tweetIt}
                    className="sd-btn sd-btn-ghost h-11 px-4"
                    data-testid="fit-share-tweet-btn"
                  >
                    <Twitter size={14} strokeWidth={1.5} /> Tweet
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-zinc-500 font-mono">Preview card auto-generates when pasted in Twitter, LinkedIn, WhatsApp, Slack, Discord.</p>
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-[#27272a]">
              <div className="sd-label mb-3 text-[#FF3B30]"><TrendingUp size={11} className="inline mr-1" strokeWidth={1.5} /> WANT MORE?</div>
              <div className="font-display text-2xl leading-tight mb-3">Score against {count?.total_brands || 12}+ brands. Get AI-drafted pitch emails. Track deals.</div>
              <p className="text-sm text-zinc-400 mb-6">
                SourceDeck's full workspace unlocks unlimited fit scores, one-click AI pitch emails,
                live YouTube analytics, and a sponsor Kanban tracker. Free for 7 days — no card required.
              </p>
              <div className="flex flex-col md:flex-row gap-3">
                <Link to="/pricing" className="sd-btn sd-btn-accent h-12 flex-1" data-testid="fit-cta-trial">
                  <Zap size={14} strokeWidth={1.5} /> Start 7-day Pro trial free <ArrowRight size={14} strokeWidth={1.5} />
                </Link>
                {urlSlug ? (
                  <Link to="/fit" className="sd-btn sd-btn-ghost h-12 flex-1" data-testid="fit-score-new">
                    Score your own channel
                  </Link>
                ) : (
                  <button
                    onClick={() => { setResult(null); setBrand(""); }}
                    className="sd-btn sd-btn-ghost h-12 flex-1"
                    data-testid="fit-score-another"
                  >
                    Score another brand
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
